<template>
  <div class="container" :class="isReady ? 'page-show' : 'page-hide'">
    <div class="detail-header">
      <h3 class="title">{{ baseInfo.gonggaoTitle }}</h3>
      <div class="detail-info flex">
        <div class="detail-info-item">
          <span>报名截止</span>
          <span class="mt-20">{{ baseInfo.endTime | formatterDateTime }}</span>
        </div>
        <div class="detail-info-item">
          <span>预算金额</span>
          <span class="mt-20 color-theme-red">{{
            baseInfo.budgetMoneyFormat
          }}</span>
        </div>
        <div class="detail-info-item">
          <span>项目状态</span>
          <span
            class="mt-20"
            :class="baseInfo.signUpUrl ? 'color-theme-red' : 'color-999'"
            >{{ baseInfo.statusName }}</span
          >
        </div>
        <div class="detail-info-item">
          <span>操作</span>
          <el-button
            type="primary"
            :disabled="!baseInfo.signUpUrl"
            class="application-btn"
            @click="openLogin"
            >{{ baseInfo.btnName }}</el-button
          >
        </div>
      </div>
    </div>
    <!-- 多标段列表 -->
    <detail-list
      v-if="sectionList.length > 1 && !sectionId"
      :list="sectionList"
    />
    <!-- 详情 -->
    <detail-info
      v-else-if="baseInfo.infoid"
      :base-info="baseInfo"
      :section-id="sectionId"
    />
  </div>
</template>

<script>
import purchaseApi from "@/api/purchase";
import { formatterDateTime } from "@/filters";
import { PROJECT_STATUS_BUTTON_TEXT } from "@/utils/constant";

export default {
  name: "ProjectInfo",
  components: {
    DetailList: () => import("./components/detailList.vue"),
    DetailInfo: () => import("./components/detailInfo.vue"),
  },

  // ============================================================
  // data
  // ============================================================
  data() {
    return {
      isReady: false,
      id: "",
      infoid: "",
      sectionId: "",
      noticeType: "1",
      baseInfo: {},
      sectionList: [],
    };
  },

  // ============================================================
  // computed —— TDK 自动响应 baseInfo 变化
  // ============================================================
  computed: {
    tdk() {
      const notice = this.baseInfo || {};
      const gonggaoTitle = notice.gonggaoTitle || "采购公告详情";
      const location = notice.projectLocation || "";
      const category =
        notice.purchaseCategory || notice.purchaseMajorCategory || "采购";
      const org = notice.organizationUnit || "";
      const budget = notice.budgetMoneyFormat || "";
      const status = notice.statusName || "";

      const title =
        gonggaoTitle + (location ? "|" + location : "") + " - 长沙阳光采购";

      const keywords = [location, category, "采购公告", org, "长沙阳光采购"]
        .filter(Boolean)
        .join(",");

      const description = org
        ? `${org}发布${category}公告，项目：${gonggaoTitle}，预算${budget}。项目${status}。更多招采信息关注长沙阳光采购。`
        : `长沙阳光采购提供${category}公告，项目：${gonggaoTitle}${
            budget ? `，预算${budget}` : ""
          }。更多招采信息关注长沙阳光采购。`;

      return { title, keywords, description };
    },
  },

  // ============================================================
  // watch —— baseInfo 变化时自动同步 TDK 到 DOM
  // ============================================================
  watch: {
    baseInfo: {
      handler(newVal) {
        if (newVal && newVal.gonggaoTitle) {
          this.syncMetaDOM();
        }
      },
      deep: true,
    },
  },

  // ============================================================
  // created
  // ============================================================
  created() {
    const query = this.$route.query;
    this.id = query.id || "";
    this.infoid = query.infoid || "";
    this.sectionId = query.sectionId ? decodeURIComponent(query.sectionId) : "";
    this.noticeType = query.noticeType || "1";
    this.getBaseInfo();
  },

  // ============================================================
  // methods
  // ============================================================
  methods: {
    formatterDateTime,

    /** 同步 TDK 到 DOM */
    syncMetaDOM() {
      const { title, keywords, description } = this.tdk;
      document.title = title;
      this.setMeta("keywords", keywords);
      this.setMeta("description", description);
    },

    /** 工具：有则更新，无则创建 */
    setMeta(name, content) {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    },

    /** 获取基础信息 */
    getBaseInfo() {
      const isInternalNotice = /^(nxzc_|cszc_)/.test(this.infoid || "");
      const params = isInternalNotice
        ? { infoid: this.infoid }
        : { id: this.id, infoid: this.infoid, sectionId: this.sectionId };

      purchaseApi.baseInfo(params).then((res) => {
        if (res.code === 0) {
          const data = res.data || {};
          const sectionList = Array.isArray(data.sectionList)
            ? data.sectionList
            : [];

          // 状态映射
          data.status_name =
            PROJECT_STATUS_BUTTON_TEXT[data.status] ||
            PROJECT_STATUS_BUTTON_TEXT.default ||
            "";

          // 标段时间格式化
          sectionList.forEach((item) => {
            item.gonggaoFbstartDateStr = formatterDateTime(
              item.gonggaoFbstartDate,
            );
          });

          this.baseInfo = data;
          this.sectionList = sectionList;

          if (sectionList.length === 1) {
            this.sectionId = sectionList[0].id;
          }

          // 同步到 store（面包屑等用）
          this.$store.commit(
            "permission/SET_DETAIL_TITLE",
            data.gonggaoTitle || "采购公告详情",
          );

          // TDK 由 watch 自动触发，不需要手动调
        }
        this.isReady = true;
      });
    },

    /** 选择公告类型 */
    selectNoticeType(type) {
      if (type === this.noticeType) return;

      this.noticeType = type;
      const query = { ...this.$route.query, noticeType: type };
      if (!/^(nxzc_|cszc_)/.test(this.infoid || "")) {
        query.id = this.id;
      }
      this.$router.replace({ path: this.$route.path, query });
      this.getDetail();
    },

    /** 跳转报名 */
    openLogin() {
      const url = this.baseInfo.signUpUrl;
      if (!url) return;

      try {
        const parsed = new URL(url);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return;

        const win = window.open();
        if (win) {
          win.opener = null;
          win.location = parsed.href;
        }
      } catch (e) {
        // URL 格式非法
      }
    },
  },
};
</script>

<style scoped lang="scss">
.container {
  padding-bottom: 20px;
}
.page-show {
  opacity: 1;
}
.page-hide {
  opacity: 0;
  transition: all 0.5s;
}
.detail-header {
  border: 1px solid #ddd;
  background-color: #fff;
  .title {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 94px;
    line-height: 36px;
    text-align: center;
    font-size: 26px;
    font-weight: bold;
  }
  .detail-info {
    border-top: 1px solid #ddd;
    padding: 30px 0;
    font-size: 16px;
    .detail-info-item {
      flex: 1;
      width: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      .application-btn {
        width: 90px;
        height: 32px;
        border-radius: 0;
        padding: 0;
        margin-top: 14px;
        &.is-disabled {
          background-color: #ddd;
          border-color: #ddd;
        }
      }
    }
    .detail-info-item + .detail-info-item {
      border-left: 1px solid #ddd;
    }
  }
}
.notice-list {
  height: 100px;
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 0 37px;
  background-color: #fff;
  .notice-item {
    width: 250px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    font-size: 20px;
    color: #999;
    cursor: not-allowed;
    img {
      width: 25px;
      height: 25px;
      margin-right: 10px;
    }
  }
  .can-view {
    cursor: pointer;
    color: $--color-primary;
  }
  .active {
    cursor: pointer;
    background: $--color-primary;
    color: #fff;
  }
}
.detail-table {
  margin-top: 20px;
  border: 1px solid #ddd;
  border-bottom: 0;
  &::before {
    height: 0;
  }
  .select-btn {
    width: 70px;
    height: 24px;
    padding: 0;
    text-align: center;
    border-radius: 0;
    &.is-active {
      background-color: $--color-primary;
      color: #fff;
    }
  }
}
.detail-content {
  background-color: #fff;
  padding: 0 20px;
  border: 1px solid #ddd;
  margin-top: 20px;
  .detail-content-header {
    height: 136px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}
</style>
